/* eslint-disable */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import InsertChartRoundedIcon from '@mui/icons-material/InsertChartRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import CompressRoundedIcon from '@mui/icons-material/CompressRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { useLocation } from 'react-router';
import serverChatServices from '@/Services/server-chat.services';
import MarkdownView from './MarkdownView';
import { FileChip, PageCard, KindIcon, MAX_FILE_BYTES, MAX_FILES, fmtSize, kindOf } from './chatFiles';
import { guideHref } from './ServerChatGuide';
import styles from './ServerChatPage.module.scss';

const POLL_MS = 3000;
const ANSWER_POLL_MS = 1500;   // faster while Claude is answering, so text appears as it's written
// Model picker (per message, remembered in this browser). Fast mode = same Opus, faster output.
const MODELS = [
	{ id: 'opus', label: 'Opus 5.5', hint: 'Best for digging into problems' },
	{ id: 'sonnet', label: 'Sonnet 5.5', hint: 'Faster, good for lookups' },
	{ id: 'haiku', label: 'Haiku 5.5', hint: 'Fastest, simple questions' },
];
const EFFORTS = ['low', 'medium', 'high', 'max'];
const DEFAULT_OPTS = { model: 'opus', effort: 'medium', fast: false };
const optsLabel = (o) => `${(MODELS.find((m) => m.id === o?.model) || MODELS[0]).label} · ${o?.effort || 'medium'}${o?.fast ? (o.fastState && o.fastState !== 'on' ? ' · fast unavailable' : ' · fast') : ''}`;
// One-click "make this into..." actions. Each prompt names the format and contents, so the bot makes it
// straight away (its rules say: ask first, unless the user asked for that exact file).
const MAKE = (fmt, contents) => (scope) => `Make ${fmt} from ${scope}. Include ${contents}. Make the file now, no need to ask.`;
const QUICK = [
	{ id: 'page', label: 'Live page', Icon: DashboardRoundedIcon, ext: '.html', main: true,
		prompt: MAKE('a live page (a self-contained dashboard)', 'the key numbers as cards, the tables (filterable) and one chart') },
	{ id: 'pptx', label: 'PowerPoint', Icon: SlideshowRoundedIcon, ext: '.pptx', main: true,
		prompt: MAKE('a PowerPoint (.pptx) deck', 'a title slide, one slide per finding, a numbers table and a next-steps slide') },
	{ id: 'docx', label: 'Word', Icon: DescriptionRoundedIcon, ext: '.docx', main: true,
		prompt: MAKE('a Word (.docx) report', 'a short summary, the findings, the tables, the cause and the suggested fix') },
	{ id: 'xlsx', label: 'Excel', Icon: TableChartRoundedIcon, ext: '.xlsx', main: true,
		prompt: MAKE('an Excel (.xlsx) workbook', 'every table as its own sheet, with headers, filters and totals') },
	{ id: 'pdf', label: 'PDF', Icon: PictureAsPdfRoundedIcon, ext: '.pdf',
		prompt: MAKE('a PDF report', 'a short summary, the findings, the tables, the cause and the suggested fix') },
	{ id: 'csv', label: 'CSV', Icon: GridOnRoundedIcon, ext: '.csv', prompt: MAKE('a CSV file', 'the main table only') },
	{ id: 'png', label: 'Chart image', Icon: InsertChartRoundedIcon, ext: '.png',
		prompt: MAKE('a PNG chart image', 'the most important numbers, clearly labelled') },
	{ id: 'email', label: 'Email summary', Icon: EmailRoundedIcon, rewrite: true,
		prompt: (scope) => `Rewrite ${scope} as an email-ready summary: a subject line, 3-5 bullet points and one action line. Plain text, no file.` },
	{ id: 'short', label: 'Shorter', Icon: CompressRoundedIcon, rewrite: true,
		prompt: (scope) => `Say ${scope} in 3 short bullet points. No file.` },
	{ id: 'simple', label: 'Simpler', Icon: LightbulbRoundedIcon, rewrite: true,
		prompt: (scope) => `Explain ${scope} in plain words for someone who is not technical. No file.` },
];
const STARTERS = [
	{ label: 'Ops dashboard for today', Icon: DashboardRoundedIcon, text: 'Make a live page: today\'s orders, dispatches and errors for my customer, as cards and a chart. Make it now, no need to ask.' },
	{ label: 'Stock report (Excel)', Icon: TableChartRoundedIcon, text: 'Make an Excel (.xlsx) stock report for my customer: current stock by store and item, with low-stock items on their own sheet. Make it now, no need to ask.' },
	{ label: 'Errors today (PDF)', Icon: PictureAsPdfRoundedIcon, text: 'Make a PDF summary of today\'s server errors for my customer: what failed, how often, and the likely cause. Make it now, no need to ask.' },
];

// Popover list of QUICK items, in two groups.
function QuickMenu({ onPick, onClose, scopeLabel }) {
	useEffect(() => {
		const k = (e) => { if (e.key === 'Escape') onClose(); };
		window.addEventListener('keydown', k);
		return () => window.removeEventListener('keydown', k);
	}, [onClose]);
	const item = (q) => (
		<button key={q.id} type='button' role='menuitem' className={styles.pickerItem} onClick={() => { onClose(); onPick(q); }}>
			<span className={styles.quickMenuRow}><q.Icon fontSize='small' aria-hidden className={styles.quickIcon} />{q.label}</span>
		</button>
	);
	return (
		<>
			<div className={styles.pickerScrim} onClick={onClose} />
			<div className={`${styles.picker} ${styles.quickMenu}`} role='menu' aria-label={`Quick actions for ${scopeLabel}`}>
				<div className={styles.pickerLabel}>Make a file · from {scopeLabel}</div>
				{QUICK.filter((q) => !q.rewrite).map(item)}
				<div className={styles.pickerSep} />
				<div className={styles.pickerLabel}>Rewrite</div>
				{QUICK.filter((q) => q.rewrite).map(item)}
			</div>
		</>
	);
}

// Row of one-click actions under an answer: 4 main formats + "More".
function QuickActions({ m, disabled, onPick }) {
	const [more, setMore] = useState(false);
	const made = (q) => q.ext && ((m.files || []).some((f) => f.name.toLowerCase().endsWith(q.ext))
		|| (q.id === 'page' && !!m.pages?.length));
	return (
		<div className={styles.quickRow} role='toolbar' aria-label='Make this answer into a file'>
			{QUICK.filter((q) => q.main).map((q) => (
				<button key={q.id} type='button' className={styles.quickBtn} disabled={disabled} onClick={() => onPick(q)}
					aria-label={`Make ${q.label} from this answer`} title={`Make ${q.label} from this answer`}>
					<q.Icon fontSize='inherit' aria-hidden className={styles.quickIcon} />
					{q.label}{made(q) && <CheckRoundedIcon fontSize='inherit' aria-label='already made' />}
				</button>
			))}
			<span className={styles.pickerWrap}>
				<button type='button' className={styles.quickBtn} disabled={disabled} onClick={() => setMore((v) => !v)}
					aria-haspopup='menu' aria-expanded={more} aria-label='More quick actions'>
					<MoreHorizRoundedIcon fontSize='inherit' aria-hidden /> More
				</button>
				{more && <QuickMenu scopeLabel='this answer' onClose={() => setMore(false)} onPick={onPick} />}
			</span>
		</div>
	);
}

const EMPTY_DB = { host: '', port: '3306', user: '', password: '', schema: 'resoee2_1' };
const EXAMPLES = [
	"Why didn't stock change after dispatch for order X?",
	'What changed in main-be this week?',
	"Show errors in today's logs",
	'Explain how GRN close updates stock',
];

// Backend times are unix seconds.
function relTime(sec) {
	if (!sec) return '';
	const d = Math.floor(Date.now() / 1000) - sec;
	if (d < 60) return 'just now';
	if (d < 3600) return `${Math.floor(d / 60)}m ago`;
	if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
	if (d < 7 * 86400) return `${Math.floor(d / 86400)}d ago`;
	return new Date(sec * 1000).toLocaleDateString();
}
const fullTime = (sec) => (sec ? new Date(sec * 1000).toLocaleString() : '');
const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const clockTime = (sec) => new Date(sec * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
// One saved Claude token's health, as the chip shows it.
const tokenStateText = (t) => (t.state === 'invalid' ? 'Invalid – replace it'
	: t.state === 'exhausted' ? `Used up until ${clockTime(t.exhaustedUntil)}` : 'Ready');

function CopyButton({ text, label }) {
	const [done, setDone] = useState(false);
	const copy = () => navigator.clipboard?.writeText(text || '').then(() => { setDone(true); setTimeout(() => setDone(false), 1500); });
	return (
		<button type='button' className={styles.iconBtnSm} onClick={copy} title={label || 'Copy'}>
			{done ? <CheckRoundedIcon fontSize='inherit' /> : <ContentCopyRoundedIcon fontSize='inherit' />}
		</button>
	);
}

// Split Claude's "### Next" follow-up questions off the answer so they can be shown as buttons.
// Also splits off a "### Glossary" section (word definitions the user can save).
function splitNext(text) {
	const src = text || '';
	const first = /\n#{2,4}\s*(Next|Glossary)\s*\n/i.exec(src);
	if (!first) return { body: src, next: [], glossary: [] };
	const out = { body: src.slice(0, first.index).trimEnd(), next: [], glossary: [] };
	const re = /\n#{2,4}\s*(Next|Glossary)\s*\n([\s\S]*?)(?=\n#{2,4}\s*(?:Next|Glossary)\s*\n|$)/gi;
	let m;
	while ((m = re.exec(src))) {
		const lines = m[2].split('\n').map((l) => l.replace(/^\s*[-*\d.]+\s*/, '').trim()).filter((l) => l && !l.startsWith('```'));
		if (m[1].toLowerCase() === 'next') out.next = lines.slice(0, 4);
		else out.glossary = lines.slice(0, 3);
	}
	return out;
}

// List of what Claude did: searches, files read, DB queries. Live while answering.
function StepList({ steps, live }) {
	const [all, setAll] = useState(false);
	if (!steps?.length) return null;
	const shown = all || !live ? steps : steps.slice(-8);
	return (
		<ol className={styles.steps}>
			{live && !all && steps.length > 8 && (
				<li className={styles.stepMore}><button type='button' onClick={() => setAll(true)}>Show all {steps.length} steps</button></li>
			)}
			{shown.map((st, k) => (
				<li key={k} className={`${styles.step} ${st.note ? styles.stepNote : ''} ${st.failed ? styles.stepFailed : ''}`}>
					<span className={styles.stepDot} />
					<span className={styles.stepText}>{st.text}{st.failed ? ' (failed)' : ''}</span>
				</li>
			))}
		</ol>
	);
}

// Collapsed "what I did" under a finished answer.
function StepsSummary({ steps, seconds }) {
	const [open, setOpen] = useState(false);
	// Customer-facing answers come without step details - show nothing.
	if (!steps?.length || !steps.some((st) => st.text)) return null;
	const tools = steps.filter((st) => !st.note).length;
	return (
		<div className={styles.stepsWrap}>
			<button type='button' className={styles.stepsToggle} onClick={() => setOpen((o) => !o)}>
				{open ? '▾' : '▸'} What I did · {tools} step{tools === 1 ? '' : 's'}{seconds ? ` · ${fmtElapsed(seconds)}` : ''}
			</button>
			{open && <StepList steps={steps} />}
		</div>
	);
}

// Friendly status lines for customer-facing answers: say roughly what's happening, never how.
const FRIENDLY = {
	start: ['Reading your question…', 'Getting things ready…', 'Understanding what you need…'],
	data: ['Looking through your records…', 'Checking your recent orders and stock…', 'Matching up the numbers…', 'Counting things up…'],
	code: ['Checking how this part works…', 'Following the process step by step…', 'Looking at how this is calculated…'],
	logs: ['Checking recent activity…', 'Looking at what happened around that time…'],
	think: ['Putting it together…', 'Double-checking the details…', 'Almost there…'],
	work: ['Working on it…', 'Digging a little deeper…'],
};
function friendlyLine(steps, secs) {
	const last = steps?.length ? steps[steps.length - 1].kind || 'work' : 'start';
	const lines = FRIENDLY[last] || FRIENDLY.work;
	if (secs > 75) return 'Still on it - this one needs a careful look…';
	return lines[Math.floor(secs / 4) % lines.length];
}

// Animated "thinking" row with a live timer and the live step list.
function Thinking({ since, steps, draft }) {
	const [now, setNow] = useState(Date.now());
	useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
	const secs = since ? Math.max(0, Math.floor(now / 1000 - since)) : 0;
	const detailed = !!steps?.some((st) => st.text);
	return (
		<div className={styles.row}>
			<div className={styles.avatar}><AutoAwesomeRoundedIcon fontSize='inherit' /></div>
			<div className={styles.botCol}>
				<div className={styles.thinking}>
					<span className={styles.dots}><i /><i /><i /></span>
					<span>{detailed ? 'Working…' : friendlyLine(steps, secs)}</span>
					<span className={styles.elapsed}>{fmtElapsed(secs)}</span>
					{detailed && secs > 60 && <span className={styles.hint}>Long answers can take a few minutes</span>}
				</div>
				{detailed && <StepList steps={steps} live />}
				{!!draft && <div className={styles.draft}><MarkdownView text={draft} /></div>}
			</div>
		</div>
	);
}

export default function ServerChatPage() {
	const [status, setStatus] = useState(null);
	const [chats, setChats] = useState([]);
	const [chat, setChat] = useState(null);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [showSetup, setShowSetup] = useState(false);
	const [db, setDb] = useState(EMPTY_DB);
	const [logsKey, setLogsKey] = useState('');
	const [notes, setNotes] = useState('');
	const [rebuildBriefs, setRebuildBriefs] = useState(true);
	const [claudeToken, setClaudeToken] = useState('');
	const [tokenLabel, setTokenLabel] = useState('');
	// Files picked for the next message: {key, file, shown, size, kind, preview, progress, state, name, err}.
	const [uploads, setUploads] = useState([]);
	const [zoom, setZoom] = useState(null);
	const [sending, setSending] = useState(false);
	const [sideOpen, setSideOpen] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [atBottom, setAtBottom] = useState(true);
	const scrollRef = useRef(null);
	const fileRef = useRef(null);
	const inputRef = useRef(null);
	const stickRef = useRef(true);
	const uploadSeq = useRef(0);
	const uploadReqs = useRef({});
	const location = useLocation();

	const patchUpload = (key, patch) => setUploads((cur) => cur.map((u) => (u.key === key ? { ...u, ...patch } : u)));

	// Each file uploads right away; the message only carries the returned names.
	const startUpload = (u) => {
		const req = serverChatServices.uploadFile(u.file, (p) => patchUpload(u.key, { progress: p }));
		uploadReqs.current[u.key] = req;
		req.then((d) => patchUpload(u.key, {
			state: 'done', progress: 1, name: d.name, shown: d.shown || u.shown, size: d.size ?? u.size, kind: d.kind || u.kind,
		}))
			.catch((e) => { if (e !== 'cancelled') patchUpload(u.key, { state: 'error', err: String(e || 'Upload failed') }); })
			.finally(() => { delete uploadReqs.current[u.key]; });
	};

	const addFiles = (files) => {
		const list = Array.from(files || []).filter(Boolean);
		if (!list.length) return;
		const tooBig = list.find((f) => f.size > MAX_FILE_BYTES);
		if (tooBig) setError(`${tooBig.name} is ${fmtSize(tooBig.size)}. Limit is 20 MB per file. Split it or export fewer rows.`);
		let ok = list.filter((f) => f.size <= MAX_FILE_BYTES);
		const room = MAX_FILES - uploads.length;
		if (ok.length > room) { setError(`Up to ${MAX_FILES} files per message.`); ok = ok.slice(0, Math.max(0, room)); }
		const fresh = ok.map((f) => {
			uploadSeq.current += 1;
			return {
				key: uploadSeq.current, file: f, shown: f.name || 'file', size: f.size, kind: kindOf(f.name),
				preview: /^image\//.test(f.type) ? URL.createObjectURL(f) : '', progress: 0, state: 'uploading', name: '', err: '',
			};
		});
		if (!fresh.length) return;
		setUploads((cur) => [...cur, ...fresh]);
		fresh.forEach(startUpload);
	};

	// ✕ on a tile: stop the upload, or remove the uploaded file from the server.
	const removeUpload = (u) => {
		if (uploadReqs.current[u.key]) uploadReqs.current[u.key].abort();
		else if (u.state === 'done' && u.name) serverChatServices.deleteUpload(u.name).catch(() => {});
		if (u.preview) URL.revokeObjectURL(u.preview);
		setUploads((cur) => cur.filter((x) => x.key !== u.key));
	};

	const retryUpload = (u) => {
		patchUpload(u.key, { state: 'uploading', progress: 0, err: '' });
		startUpload(u);
	};

	// Leaving the page: stop uploads still running and free the image previews.
	const uploadsRef = useRef(uploads);
	uploadsRef.current = uploads;
	useEffect(() => () => {
		Object.values(uploadReqs.current).forEach((r) => r.abort());
		uploadsRef.current.forEach((u) => u.preview && URL.revokeObjectURL(u.preview));
	}, []);

	// After a send the server keeps the files: drop only the tiles that went out (not ones added meanwhile).
	const clearUploads = (keys) => setUploads((cur) => {
		cur.forEach((u) => keys.includes(u.key) && u.preview && URL.revokeObjectURL(u.preview));
		return cur.filter((u) => !keys.includes(u.key));
	});

	const onPaste = (e) => {
		const files = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === 'file').map((it) => it.getAsFile());
		if (files.length) { e.preventDefault(); addFiles(files); }
	};

	const run = useCallback((action, body) => {
		setError('');
		return serverChatServices.chatAction(action, body).then((res) => res?.data)
			.catch((e) => { setError(String(e || 'Something went wrong')); throw e; });
	}, []);

	const loadStatus = useCallback(() => run('status').then((s) => {
		if (!s) return;
		setStatus(s);
		setNotes((n) => (n === '' ? s.notes || '' : n));
		setDb((d) => (d.host ? d : { ...EMPTY_DB, ...Object.fromEntries(Object.entries(s.db || {}).filter(([, v]) => v)), password: '' }));
	}).catch(() => {}), [run]);

	const loadChats = useCallback(() => run('list').then((d) => d && setChats(d.chats || [])).catch(() => {}), [run]);

	useEffect(() => { loadStatus(); loadChats(); }, [loadStatus, loadChats]);

	// Poll while Claude is answering.
	const running = chat?.status === 'running';
	const waiting = running || chat?.status === 'queued';   // queued: poll so it flips to running by itself
	useEffect(() => {
		if (!waiting) return undefined;
		const t = setInterval(() => {
			run('get', { chatId: chat.id }).then((d) => {
				if (d?.chat) setChat(d.chat);
				if (d?.chat?.status !== 'running') { loadChats(); loadStatus(); }
			}).catch(() => {});
		}, running ? ANSWER_POLL_MS : POLL_MS);
		return () => clearInterval(t);
	}, [waiting, running, chat?.id, run, loadChats, loadStatus]);

	// Poll while Setup or "Learn" is running.
	const bgRunning = !!(status?.setupRunning || status?.learnRunning || status?.briefsRunning);
	useEffect(() => {
		if (!bgRunning) return undefined;
		const t = setInterval(loadStatus, POLL_MS);
		return () => clearInterval(t);
	}, [bgRunning, loadStatus]);

	// Auto-scroll only when the user is already near the bottom.
	const onScroll = () => {
		const el = scrollRef.current;
		if (!el) return;
		const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
		stickRef.current = near;
		setAtBottom(near);
	};
	const scrollToBottom = (smooth) => {
		const el = scrollRef.current;
		if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
	};
	useLayoutEffect(() => { if (stickRef.current) scrollToBottom(false); }, [chat?.messages?.length, running]);
	useLayoutEffect(() => { stickRef.current = true; setAtBottom(true); scrollToBottom(false); }, [chat?.id]);

	// Grow the composer with its text.
	useLayoutEffect(() => {
		const el = inputRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
	}, [message]);

	const openChat = (id) => {
		setSideOpen(false);
		run('get', { chatId: id }).then((d) => d && setChat(d.chat)).catch(() => {});
	};

	// "Open chat" on a page view links here with ?chat=<id>.
	useEffect(() => {
		const id = new URLSearchParams(location.search).get('chat');
		if (id) openChat(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const newChat = () => { setChat(null); setSideOpen(false); setTimeout(() => inputRef.current?.focus(), 0); };

	// Answer style: technical (default) or customer-facing. Remembered in this browser only.
	const [technical, setTechnical] = useState(() => {
		try { return window.localStorage.getItem('serverChat.technical') !== '0'; } catch (e) { return true; }
	});
	const [opts, setOptsState] = useState(() => {
		try { return { ...DEFAULT_OPTS, ...JSON.parse(window.localStorage.getItem('serverChat.opts') || '{}') }; } catch (e) { return DEFAULT_OPTS; }
	});
	const [pickerOpen, setPickerOpen] = useState(false);
	const setOpts = (patch) => setOptsState((o) => {
		const n = { ...o, ...patch };
		if (n.model !== 'opus') n.fast = false;   // fast mode is Opus-only
		try { window.localStorage.setItem('serverChat.opts', JSON.stringify(n)); } catch (e) { /* private mode */ }
		return n;
	});
	const toggleTechnical = () => setTechnical((t) => {
		try { window.localStorage.setItem('serverChat.technical', t ? '0' : '1'); } catch (e) { /* private mode */ }
		return !t;
	});

	// imgs: only when re-asking an old message that still carries base64 images.
	const ask = (text, attachments, chatId, imgs) => {
		setSending(true);
		stickRef.current = true;
		return run('ask', { chatId, message: text || '(see the attached files)', attachments: attachments || [],
			...(imgs?.length ? { images: imgs } : {}), style: technical ? 'technical' : 'plain', ...opts })
			.then((d) => { if (d?.chat) { setChat(d.chat); loadChats(); } return d; })
			.finally(() => setSending(false));
	};

	const uploading = uploads.some((u) => u.state === 'uploading');
	const uploadFailed = uploads.some((u) => u.state === 'error');
	const readyUploads = uploads.filter((u) => u.state === 'done');
	const ready = readyUploads.map((u) => u.name);

	const send = () => {
		const text = message.trim();
		if ((!text && !ready.length) || sending || uploading || uploadFailed) return;   // while running it waits in line
		const sentKeys = readyUploads.map((u) => u.key);
		ask(text, ready, chat?.id).then((d) => { if (d?.chat) { setMessage(''); clearUploads(sentKeys); } }).catch(() => {});
	};

	// Re-send the user message that came right before a failed answer.
	const retry = (idx) => {
		if (sending) return;
		const prev = chat?.messages?.slice(0, idx).reverse().find((m) => m.role === 'user');
		// Sent files were already moved into the chat, so only the text (and old inline images) go again.
		if (prev) ask(prev.text, [], chat.id, prev.images).catch(() => {});
	};

	const deleteChat = (id, isRunning) => {
		if (isRunning) { setError('Wait for the answer to finish first'); return; }
		if (!window.confirm('Delete this chat and everything Claude remembers of it?')) return;
		run('clear', { chatId: id }).then(() => { if (chat?.id === id) setChat(null); loadChats(); }).catch(() => {});
	};

	// Glossary: one line from a chat answer, or the whole text from Settings.
	const [savedGlossary, setSavedGlossary] = useState({});
	const [glossaryText, setGlossaryText] = useState(null);
	const saveGlossaryLine = (line) => {
		run('addGlossary', { line }).then((d) => {
			setSavedGlossary((s) => ({ ...s, [line]: true }));
			if (d?.glossary) setStatus((st) => ({ ...st, glossary: d.glossary }));
		}).catch(() => {});
	};
	const saveGlossaryAll = () => {
		run('saveGlossary', { text: glossaryText ?? status?.glossary ?? '' }).then((d) => {
			if (d?.glossary !== undefined) { setStatus((st) => ({ ...st, glossary: d.glossary })); setGlossaryText(null); }
		}).catch(() => {});
	};

	const startBriefs = () => {
		run('briefs').then((s) => { if (s) setStatus(s); }).catch(() => {});
	};

	const startLearn = () => {
		run('learn').then((s) => { if (s) setStatus(s); }).catch(() => {});
	};

	const startSetup = () => {
		run('setup', { db, logsKey, notes, rebuildBriefs }).then((s) => {
			if (s) setStatus(s);
			setDb((d) => ({ ...d, password: '' }));
			setLogsKey('');
		}).catch(() => {});
	};

	// The signed-out banner saves one token as 'default'; Settings names each one.
	const saveClaudeToken = (label) => {
		run('addClaudeToken', { label, token: claudeToken }).then((s) => {
			if (s) setStatus(s);
			setClaudeToken('');
			setTokenLabel('');
		}).catch(() => {});
	};
	const removeToken = (label) => {
		if (!window.confirm(`Remove the Claude token "${label}"?`)) return;
		run('removeClaudeToken', { label }).then((s) => { if (s) setStatus(s); }).catch(() => {});
	};
	// List order = which token is tried first.
	const moveToken = (label, dir) => {
		run('moveClaudeToken', { label, dir }).then((s) => { if (s) setStatus(s); }).catch(() => {});
	};
	const claudeTokens = status?.claudeTokens || [];

	const msgs = chat?.messages || [];
	const lastMsg = msgs[msgs.length - 1];
	// Older backends don't send claudeInstalled - treat missing as installed.
	const claudeMissing = status?.claudeInstalled === false;
	const needsClaudeLogin = status && !claudeMissing && (!status.loggedIn || lastMsg?.authError);
	const needsSetup = !!status?.needsSetup;
	const setupMissing = status && !status.setupDone && !needsSetup;
	const lastUserAt = [...msgs].reverse().find((m) => m.role === 'user')?.at;
	const busy = sending;   // a running answer no longer blocks sending: new messages wait in line
	const canSend = !busy && !uploading && !uploadFailed && (message.trim() || ready.length);
	const sendTitle = uploading ? 'Waiting for uploads…' : uploadFailed ? 'Remove or retry the failed file'
		: waiting ? 'Add to the line (sent after the current answer)' : 'Send';

	// Stop the running answer, and manage messages waiting in line.
	const [editQ, setEditQ] = useState(null);   // {id, text} while a waiting message is being edited
	const stop = () => run('stop', { chatId: chat.id }).then((d) => d?.chat && setChat(d.chat)).catch(() => {});
	const queueAct = (action, qid, extra) => run(action, { chatId: chat.id, qid, ...(extra || {}) })
		.then((d) => { if (d?.chat) setChat(d.chat); loadChats(); }).catch(() => {});
	const queue = chat?.queue || [];
	// "How Server Chat works": a permanent page (/internal/server-chat/guide), opened from Settings.
	const openGuide = () => window.open(guideHref(), '_blank', 'noopener');
	const [makeOpen, setMakeOpen] = useState(false);
	const [toast, setToast] = useState('');
	const quick = (q, scope) => {
		ask(q.prompt(scope), [], chat?.id).then((d) => {
			if (d?.queued) { setToast(`${q.label}: added to the line`); setTimeout(() => setToast(''), 2500); }
		}).catch(() => {});
	};
	useEffect(() => {   // Esc stops the running answer (when no menu is open)
		if (!running) return undefined;
		const k = (e) => { if (e.key === 'Escape' && !pickerOpen && !makeOpen) stop(); };
		window.addEventListener('keydown', k);
		return () => window.removeEventListener('keydown', k);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [running, pickerOpen, makeOpen, chat?.id]);

	// Settings: reset everything Server Chat has made (the server checks who may do it).
	const [resetSummary, setResetSummary] = useState(null);
	const [resetText, setResetText] = useState('');
	const [resetDone, setResetDone] = useState(false);
	const loadResetSummary = useCallback(() => {
		serverChatServices.chatAction('resetSummary').then((res) => setResetSummary(res?.data || null)).catch(() => setResetSummary(null));
	}, []);
	useEffect(() => { if (showSetup) { setResetDone(false); loadResetSummary(); } }, [showSetup, loadResetSummary]);
	const doReset = () => {
		if (resetText !== 'RESET') return;
		run('reset', { confirm: 'RESET' }).then((d) => {
			setResetText('');
			setResetDone(d?.failed?.length ? d.failed : true);
			setChat(null);
			loadChats();
			loadStatus();
			loadResetSummary();
		}).catch(() => {});
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		if (!busy) addFiles(e.dataTransfer?.files);
	};

	return (
		<div className={styles.page}>
			{sideOpen && <div className={styles.scrim} onClick={() => setSideOpen(false)} />}

			<aside className={`${styles.side} ${sideOpen ? styles.sideOpen : ''}`}>
				<div className={styles.sideHead}>
					<span className={styles.brand}><AutoAwesomeRoundedIcon fontSize='inherit' /> Server Chat</span>
					<button type='button' className={styles.iconBtn} onClick={() => setShowSetup(true)} title='Settings'>
						<SettingsOutlinedIcon fontSize='small' />
					</button>
				</div>
				<button type='button' className={styles.newBtn} onClick={newChat}>
					<AddRoundedIcon fontSize='small' /> New chat
				</button>
				<div className={styles.sideLabel}>Recent</div>
				<div className={styles.chatList}>
					{!chats.length && <div className={styles.sideEmpty}>No chats yet</div>}
					{chats.map((c) => (
						<div
							key={c.id}
							className={`${styles.chatItem} ${chat?.id === c.id ? styles.chatItemActive : ''}`}
							onClick={() => openChat(c.id)}
						>
							<div className={styles.chatItemText}>
								<div className={styles.chatTitle}>{c.title || 'Untitled'}</div>
								<div className={styles.chatMeta}>
									{c.status === 'running' && <span className={styles.pulse} title='Answering' />}
									{c.status === 'queued' && <span className={styles.queuedDot} title='Waiting in line' />}
									{c.status === 'error' && <span className={styles.errDot} title='Last answer failed' />}
									{relTime(c.updatedAt)}
								</div>
							</div>
							<button
								type='button'
								className={styles.chatDel}
								title='Delete chat'
								onClick={(e) => { e.stopPropagation(); deleteChat(c.id, c.status === 'running'); }}
							>
								<DeleteOutlineIcon fontSize='inherit' />
							</button>
						</div>
					))}
				</div>
				{status && (
					<div className={styles.sideFoot}>
						<span className={status.loggedIn ? styles.okDot : styles.errDot} />
						Claude {status.loggedIn ? 'signed in' : 'signed out'}
						{!!status.activeToken && <><span className={styles.sep}>·</span>Using: {status.activeToken}</>}
						<span className={styles.sep}>·</span>
						Code pulled {status.lastPullDate || 'never'}
					</div>
				)}
			</aside>

			<main
				className={styles.main}
				onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes('Files')) { e.preventDefault(); setDragging(true); } }}
				onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
				onDrop={onDrop}
			>
				<header className={styles.head}>
					<button type='button' className={`${styles.iconBtn} ${styles.menuBtn}`} onClick={() => setSideOpen(true)} title='Chats'>
						<MenuRoundedIcon fontSize='small' />
					</button>
					<div className={styles.headTitle}>{chat ? chat.title : 'New chat'}</div>
					{chat?.status === 'queued'
						? <span className={styles.chip} title='Starts by itself when the answer ahead finishes'>
							Waiting in line{chat.queuePosition ? ` (#${chat.queuePosition})` : ''}</span>
						: status?.busy && !running && <span className={styles.chip}>Claude is busy{status?.queueLen ? ` · ${status.queueLen} waiting` : ''}</span>}
					{!!chat?.costUsd && <span className={styles.chip} title='Total cost of this chat'>${Number(chat.costUsd).toFixed(2)}</span>}
					{chat && (
						<button type='button' className={styles.iconBtn} onClick={() => deleteChat(chat.id, running)} title='Delete chat' disabled={running}>
							<DeleteOutlineIcon fontSize='small' />
						</button>
					)}
					<button type='button' className={`${styles.styleToggle} ${technical ? styles.styleToggleOn : ''}`} onClick={toggleTechnical}
						title={technical ? 'Answers include tables, files and queries. Click for customer-facing answers.'
							: 'Answers use plain business words only. Click to include technical detail.'}>
						{technical ? 'Technical detail: ON' : 'Customer-facing'}
					</button>
					<button type='button' className={`${styles.iconBtn} ${styles.headGear}`} onClick={() => setShowSetup(true)} title='Settings'>
						<SettingsOutlinedIcon fontSize='small' />
					</button>
				</header>

				{(claudeMissing || needsClaudeLogin || setupMissing || needsSetup || error) && (
					<div className={styles.banners}>
						{error && (
							<div className={`${styles.banner} ${styles.bannerErr}`}>
								<span>{error}</span>
								<button type='button' className={styles.iconBtnSm} onClick={() => setError('')}><CloseRoundedIcon fontSize='inherit' /></button>
							</div>
						)}
						{claudeMissing && (
							<div className={`${styles.banner} ${styles.bannerErr}`}>
								<span><strong>Claude isn't installed on this server.</strong> Server Chat only works on the dev server.</span>
							</div>
						)}
						{needsClaudeLogin && (
							<div className={`${styles.banner} ${styles.bannerWarn}`}>
								{claudeTokens.length ? (
									// Tokens already saved: don't overwrite 'default' from here - fix them in the tokens card.
									<>
										<span><strong>Claude is signed out.</strong> The saved Claude tokens stopped working. Add a new one or replace a broken one.</span>
										<button type='button' className={styles.btnPrimary} onClick={() => setShowSetup(true)}>Open Claude tokens</button>
									</>
								) : (
									<div className={styles.bannerBody}>
										<strong>Claude is signed out.</strong> On the dev server run <code>claude setup-token</code>, then paste the token here.
										<div className={styles.tokenRow}>
											<input type='password' placeholder='Paste Claude token' value={claudeToken} onChange={(e) => setClaudeToken(e.target.value)} />
											<button type='button' className={styles.btnPrimary} onClick={() => saveClaudeToken('default')} disabled={!claudeToken.trim()}>Save token</button>
										</div>
									</div>
								)}
							</div>
						)}
						{needsSetup && (
							<div className={`${styles.banner} ${styles.bannerWarn}`}>
								<span><strong>Server Chat needs Setup (it was reset or updated). Run Setup.</strong> Claude can't answer until Setup has run again.</span>
								<button type='button' className={styles.btnPrimary} onClick={() => setShowSetup(true)}>Run Setup</button>
							</div>
						)}
						{setupMissing && (
							<div className={`${styles.banner} ${styles.bannerInfo}`}>
								<span><strong>Setup not done yet.</strong> Claude needs the repos and DB login before it can answer.</span>
								<button type='button' className={styles.btnPrimary} onClick={() => setShowSetup(true)}>Open setup</button>
							</div>
						)}
					</div>
				)}

				<div className={styles.thread} ref={scrollRef} onScroll={onScroll}>
					<div className={styles.threadInner}>
						{!chat && (
							<div className={styles.empty}>
								<div className={styles.emptyLogo}><AutoAwesomeRoundedIcon fontSize='inherit' /></div>
								<h2>What should I dig into?</h2>
								<p>Ask about an order or stock number that looks wrong, what some code does, or recent errors in the logs. Mention the customer or store if you know it.</p>
								<div className={styles.starters} aria-label='Quick starts'>
									{STARTERS.map((st) => (
										<button key={st.label} type='button' className={styles.quickBtn}
											onClick={() => { setMessage(st.text); inputRef.current?.focus(); }}>
											<st.Icon fontSize='inherit' aria-hidden className={styles.quickIcon} /> {st.label}
										</button>
									))}
								</div>
								<div className={styles.examples}>
									{EXAMPLES.map((ex) => (
										<button key={ex} type='button' className={styles.example} onClick={() => { setMessage(ex); inputRef.current?.focus(); }}>
											{ex}
										</button>
									))}
								</div>
							</div>
						)}

						{msgs.map((m, i) => (m.role === 'user' ? (
							<div key={i} className={`${styles.row} ${styles.rowUser}`}>
								<div className={styles.userCol}>
									{!!m.images?.length && (
										<div className={styles.imgs}>
											{m.images.map((src, j) => <img key={j} src={src} alt='attached' onClick={() => setZoom(src)} />)}
										</div>
									)}
									{!!m.attachments?.length && (
										<div className={`${styles.fileTiles} ${styles.fileTilesUser}`}>
											{m.attachments.map((a) => (
												<FileChip key={a.name} chatId={chat?.id} area='in' name={a.name} shown={a.shown}
													size={a.size} kind={a.kind} onError={setError} />
											))}
										</div>
									)}
									<div className={styles.userBubble}>{m.text}</div>
									<div className={styles.msgTools}>
										<span className={styles.time}>{fullTime(m.at)}</span>
										<CopyButton text={m.text} label='Copy message' />
									</div>
								</div>
							</div>
						) : (
							<div key={i} className={styles.row}>
								<div className={styles.avatar}><AutoAwesomeRoundedIcon fontSize='inherit' /></div>
								<div className={styles.botCol}>
									{m.error ? (
										<div className={styles.errBox}>
											<div className={styles.errTitle}>{m.authError ? 'Claude is signed out' : 'Something went wrong'}</div>
											<div className={styles.errText}>{m.error}</div>
											{!m.authError && i === msgs.length - 1 && (
												<button type='button' className={styles.btnGhost} onClick={() => retry(i)} disabled={busy}>
													<ReplayRoundedIcon fontSize='inherit' /> Ask again
												</button>
											)}
										</div>
									) : <MarkdownView text={splitNext(m.text).body} />}
									{m.stopped && <span className={styles.stoppedTag}>Stopped</span>}
									{!!m.opts && <span className={styles.optsTag}>{optsLabel(m.opts)}{m.seconds ? ` · ${m.seconds}s` : ''}</span>}
									{(!!m.pages?.length || !!m.files?.length) && (
										<div className={styles.fileTiles}>
											{(m.pages || []).map((p) => <PageCard key={p.pageId} chatId={chat?.id} page={p} onError={setError} />)}
											{/* A page's own .html is already shown as a page card. */}
											{(m.files || []).filter((f) => !(m.pages || []).some((p) => p.name === f.name)).map((f) => (
												<FileChip key={f.name} chatId={chat?.id} area='out' name={f.name} size={f.size} kind={f.kind} onError={setError} />
											))}
										</div>
									)}
									<StepsSummary steps={m.steps} seconds={m.seconds} />
									{!m.error && i === msgs.length - 1 && !!splitNext(m.text).next.length && (
										<div className={styles.nextChips}>
											{splitNext(m.text).next.map((q) => (
												<button key={q} type='button' className={styles.nextChip} disabled={busy}
													onClick={() => ask(q, [], chat?.id).catch(() => {})}>{q}</button>
											))}
										</div>
									)}
									{!m.error && !m.stopped && i === msgs.length - 1 && !!m.text && (
										<QuickActions m={m} disabled={sending} onPick={(q) => quick(q, 'your answer above')} />
									)}
									{!m.error && m.style === 'technical' && splitNext(m.text).glossary.map((g) => (
										<div key={g} className={styles.glossaryRow}>
											<span className={styles.glossaryText}>📖 {g}</span>
											{savedGlossary[g]
												? <span className={styles.glossarySaved}>Saved</span>
												: <button type='button' className={styles.btnGhost} onClick={() => saveGlossaryLine(g)}>Save to glossary</button>}
										</div>
									))}
									<div className={styles.msgTools}>
										{!m.error && <CopyButton text={m.text} label='Copy answer' />}
										<span className={styles.time}>{fullTime(m.at)}</span>
									</div>
								</div>
							</div>
						)))}

						{running && <Thinking since={lastUserAt} steps={chat?.steps} draft={chat?.draft} />}
					</div>
				</div>

				{!atBottom && (
					<button type='button' className={styles.jump} onClick={() => scrollToBottom(true)}>
						<KeyboardArrowDownRoundedIcon fontSize='small' /> Jump to latest
					</button>
				)}

				<div className={styles.composerWrap}>
					{!!queue.length && (
						<div className={styles.queue}>
							<div className={styles.queueHead}>Waiting to send ({queue.length}) — sent together after the current answer</div>
							{queue.map((q) => (
								<div key={q.id} className={styles.queueItem}>
									{editQ?.id === q.id ? (
										<>
											<textarea className={styles.queueEdit} value={editQ.text} autoFocus rows={2}
												onChange={(e) => setEditQ({ ...editQ, text: e.target.value })} />
											<button type='button' className={styles.btnGhost} disabled={!editQ.text.trim()}
												onClick={() => queueAct('queueEdit', q.id, { text: editQ.text }).then(() => setEditQ(null))}>Save</button>
											<button type='button' className={styles.btnGhost} onClick={() => setEditQ(null)}>Cancel</button>
										</>
									) : (
										<>
											<span className={styles.queueText}>{q.text}{!!q.attachments?.length && ` · ${q.attachments.length} file(s)`}</span>
											<button type='button' className={styles.iconBtn} title='Send now (stops the current answer of this chat)'
												onClick={() => queueAct('sendNow', q.id)}><BoltRoundedIcon fontSize='small' /></button>
											<button type='button' className={styles.iconBtn} title='Edit'
												onClick={() => setEditQ({ id: q.id, text: q.text })}><EditRoundedIcon fontSize='small' /></button>
											<button type='button' className={styles.iconBtn} title='Remove'
												onClick={() => queueAct('queueRemove', q.id)}><CloseRoundedIcon fontSize='small' /></button>
										</>
									)}
								</div>
							))}
						</div>
					)}
					<div className={`${styles.composer} ${dragging ? styles.composerDrag : ''}`}>
						{!!uploads.length && (
							<div className={styles.pending}>
								{uploads.map((u) => (
									<div key={u.key} className={`${styles.upTile} ${u.state === 'error' ? styles.upTileErr : ''}`}>
										{u.preview
											? <img className={styles.upThumb} src={u.preview} alt={u.shown} onClick={() => setZoom(u.preview)} />
											: <span className={styles.fileIcon}><KindIcon name={u.shown} kind={u.kind} /></span>}
										<span className={styles.fileText}>
											<span className={styles.fileName}>{u.shown}</span>
											{u.state === 'error' ? (
												<span className={styles.upErr}>
													Upload failed
													<button type='button' className={styles.upRetry} onClick={() => retryUpload(u)}>Retry</button>
												</span>
											) : (
												<span className={styles.fileMeta}>
													{fmtSize(u.size)}{u.state === 'uploading' ? ` · ${Math.round((u.progress || 0) * 100)}%` : ''}
												</span>
											)}
											{u.state === 'uploading' && (
												<span className={styles.upBar}><i style={{ width: `${Math.round((u.progress || 0) * 100)}%` }} /></span>
											)}
										</span>
										<button type='button' className={styles.upX} title='Remove' onClick={() => removeUpload(u)}>
											<CloseRoundedIcon fontSize='inherit' />
										</button>
									</div>
								))}
							</div>
						)}
						<textarea
							ref={inputRef}
							rows={1}
							placeholder={waiting ? 'Type your next question — it waits in line and runs after the current answer' : 'Ask anything, or attach / paste a file'}
							onPaste={onPaste}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
						/>
						<div className={styles.composerBar}>
							<input ref={fileRef} type='file' multiple hidden
								onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
							<button type='button' className={styles.iconBtn} title={`Attach files (up to ${MAX_FILES}, 20 MB each)`}
								onClick={() => fileRef.current?.click()} disabled={busy || uploads.length >= MAX_FILES}>
								<AttachFileRoundedIcon fontSize='small' />
							</button>
							{chat && (
								<div className={styles.pickerWrap}>
									<button type='button' className={styles.pickerBtn} disabled={sending} onClick={() => setMakeOpen((v) => !v)}
										aria-haspopup='menu' aria-expanded={makeOpen} title='Turn this whole chat into a page, deck, report...'>
										<AutoFixHighRoundedIcon fontSize='inherit' aria-hidden /> <span className={styles.makeLabel}>Make…</span>
									</button>
									{makeOpen && <QuickMenu scopeLabel='this whole chat' onClose={() => setMakeOpen(false)}
										onPick={(q) => quick(q, 'everything in this chat so far')} />}
								</div>
							)}
							<div className={styles.pickerWrap}>
								<button type='button' className={styles.pickerBtn} onClick={() => setPickerOpen((v) => !v)} title='Model, effort and fast mode'>
									{optsLabel(opts)} <KeyboardArrowUpRoundedIcon fontSize='inherit' />
								</button>
								{pickerOpen && (
									<>
										<div className={styles.pickerScrim} onClick={() => setPickerOpen(false)} />
										<div className={styles.picker}>
											{MODELS.map((m, i) => (
												<button key={m.id} type='button' className={styles.pickerItem} onClick={() => setOpts({ model: m.id })}>
													<span><span className={styles.pickerName}>{m.label}</span><span className={styles.pickerHint}>{m.hint}</span></span>
													{opts.model === m.id ? <CheckRoundedIcon fontSize='small' /> : <span className={styles.pickerNum}>{i + 1}</span>}
												</button>
											))}
											<div className={styles.pickerSep} />
											<div className={styles.pickerLabel}>Effort</div>
											<div className={styles.pickerSeg}>
												{EFFORTS.map((e) => (
													<button key={e} type='button' className={opts.effort === e ? styles.pickerSegOn : ''}
														onClick={() => setOpts({ effort: e })}>{e}</button>
												))}
											</div>
											<div className={styles.pickerSep} />
											<div className={styles.pickerLabel}>Fast mode</div>
											<label className={`${styles.pickerItem} ${opts.model !== 'opus' ? styles.pickerDisabled : ''}`}
												title={opts.model !== 'opus' ? 'Fast mode works with Opus only' : 'Same Opus, faster answers (costs more)'}>
												<span className={styles.pickerName}>Enable fast mode</span>
												<input type='checkbox' className={styles.switch} checked={!!opts.fast} disabled={opts.model !== 'opus'}
													onChange={(e) => setOpts({ fast: e.target.checked })} />
											</label>
										</div>
									</>
								)}
							</div>
							<span className={styles.composerHint}>Enter to send · Shift+Enter for new line</span>
							{running && (
								<button type='button' className={styles.stopBtn} onClick={stop} title='Stop this answer (Esc)' aria-label='Stop this answer'>
									<StopRoundedIcon fontSize='small' />
								</button>
							)}
							{/* While answering with an empty box, Stop takes the Send button's place. */}
							{!(running && !message.trim() && !ready.length) && (
								<button type='button' className={styles.sendBtn} onClick={send} disabled={!canSend} title={sendTitle}>
									<ArrowUpwardRoundedIcon fontSize='small' />
								</button>
							)}
						</div>
						{dragging && <div className={styles.dropHint}>Drop files to attach</div>}
						<div className={styles.srOnly} aria-live='polite'>{toast}</div>
						{!!toast && <div className={styles.toast}>{toast}</div>}
					</div>
				</div>
			</main>

			{showSetup && (
				<div className={styles.drawerScrim} onClick={() => setShowSetup(false)}>
					<div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
						<div className={styles.drawerHead}>
							<strong>Settings &amp; setup</strong>
							<button type='button' className={styles.iconBtn} onClick={() => setShowSetup(false)} title='Close'><CloseRoundedIcon fontSize='small' /></button>
						</div>
						<div className={styles.drawerBody}>
							<section className={`${styles.card} ${styles.guideCard}`}>
								<div>
									<div className={styles.cardTitle}>How Server Chat works</div>
									<div className={styles.muted}>How to use it, what happens behind the scenes, limits and safety.</div>
								</div>
								<button type='button' className={styles.btnPrimary} onClick={openGuide}>
									<MenuBookRoundedIcon fontSize='inherit' aria-hidden /> Open guide
								</button>
							</section>
							<section className={styles.card}>
								<div className={styles.cardTitle}>Claude tokens</div>
								<div className={styles.muted}>
									Tried top to bottom. When one is used up or stops working, the next one answers.
									{!!status?.activeToken && <> Using: <strong>{status.activeToken}</strong>.</>}
								</div>
								{claudeTokens.length ? (
									<div className={styles.tokenList}>
										{claudeTokens.map((t, i) => (
											<div key={t.label} className={styles.tokenItem}>
												<div className={styles.tokenMain}>
													<div className={styles.tokenName}>
														{t.label} {!!t.last4 && <span className={styles.tokenTail}>••••{t.last4}</span>}
													</div>
													<div className={styles.tokenMeta}>
														<span className={`${styles.tokenChip} ${styles[`token_${t.state}`] || ''}`} title={t.lastError || ''}>
															{tokenStateText(t)}
														</span>
														{t.lastUsedAt ? `Last used ${relTime(t.lastUsedAt)}` : 'Not used yet'}
													</div>
												</div>
												<button type='button' className={`${styles.iconBtn} ${styles.iconBtnSm}`} title='Try this one earlier'
													onClick={() => moveToken(t.label, 'up')} disabled={i === 0}>
													<KeyboardArrowUpRoundedIcon fontSize='inherit' />
												</button>
												<button type='button' className={`${styles.iconBtn} ${styles.iconBtnSm}`} title='Try this one later'
													onClick={() => moveToken(t.label, 'down')} disabled={i === claudeTokens.length - 1}>
													<KeyboardArrowDownRoundedIcon fontSize='inherit' />
												</button>
												<button type='button' className={`${styles.iconBtn} ${styles.iconBtnSm}`} title='Remove token' onClick={() => removeToken(t.label)}>
													<DeleteOutlineIcon fontSize='inherit' />
												</button>
											</div>
										))}
									</div>
								) : (
									<div className={styles.muted}>
										No tokens saved{status?.loggedIn ? ' - using the server\'s own Claude login.' : ' yet.'}
									</div>
								)}
								<div className={styles.grid}>
									<label>
										Identifier
										<input placeholder='e.g. Shruk – Max plan' maxLength={40} value={tokenLabel} onChange={(e) => setTokenLabel(e.target.value)} />
									</label>
									<label>
										Token value
										<input type='password' placeholder='from `claude setup-token`' value={claudeToken} onChange={(e) => setClaudeToken(e.target.value)} />
									</label>
								</div>
								<div className={styles.muted}>
									Run <code>claude setup-token</code> on the dev server to get a token. Same identifier again replaces that token.
								</div>
								<button type='button' className={`${styles.btnPrimary} ${styles.tokenAdd}`} onClick={() => saveClaudeToken(tokenLabel.trim())}
									disabled={!tokenLabel.trim() || !claudeToken.trim()}>
									Add token
								</button>
							</section>

							<section className={styles.card}>
								<div className={styles.cardTitle}>Setup / Rebuild</div>
								<div className={styles.muted}>
									Creates the chat folder on the dev server, clones or pulls the repos, and has Claude write a short guide per repo.
									Blank fields keep what is already saved. Folder: <code>{status?.root}</code>
								</div>
								<div className={styles.grid}>
									<label>DB host<input value={db.host} onChange={(e) => setDb({ ...db, host: e.target.value })} /></label>
									<label>Port<input value={db.port} onChange={(e) => setDb({ ...db, port: e.target.value })} /></label>
									<label>DB user (read-only)<input value={db.user} onChange={(e) => setDb({ ...db, user: e.target.value })} /></label>
									<label>
										Password {status?.hasDbPassword ? '(saved)' : ''}
										<input type='password' value={db.password} onChange={(e) => setDb({ ...db, password: e.target.value })} />
									</label>
									<label>Schema<input value={db.schema} onChange={(e) => setDb({ ...db, schema: e.target.value })} /></label>
									<label>
										Logs key {status?.hasLogsKey ? '(saved)' : ''}
										<input type='password' value={logsKey} onChange={(e) => setLogsKey(e.target.value)} />
									</label>
								</div>
								<label className={styles.block}>
									Extra notes for Claude (optional)
									<textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
								</label>
								<label className={styles.check}>
									<input type='checkbox' checked={rebuildBriefs} onChange={(e) => setRebuildBriefs(e.target.checked)} />
									Rewrite repo guides (slow, a few minutes per repo)
								</label>
								<button type='button' className={styles.btnPrimary} onClick={startSetup} disabled={bgRunning}>
									{status?.setupRunning ? 'Setup running…' : 'Run setup'}
								</button>
								<button type='button' className={styles.btnGhost} onClick={startBriefs} disabled={bgRunning || busy}
									title='Pulls the latest code and updates only the guides of repos that changed'>
									{status?.briefsRunning ? 'Updating guides…' : 'Update repo guides (changed repos only)'}
								</button>
								{!!status?.setupLog?.length && <pre className={styles.log}>{status.setupLog.join('\n')}</pre>}
							</section>

							<section className={styles.card}>
								<div className={styles.cardTitle}>Glossary - team words</div>
								<p className={styles.cardHint}>
									What your words mean in the code, e.g. <code>"order" = PurchaseOrder</code>. Claude reads this on every
									new chat but can never edit it. Lines saved from chats are added at the bottom.
								</p>
								<textarea className={styles.glossaryBox} rows={10}
									value={glossaryText ?? status?.glossary ?? ''} onChange={(e) => setGlossaryText(e.target.value)} />
								<button type='button' className={styles.btnPrimary} onClick={saveGlossaryAll}
									disabled={glossaryText === null || glossaryText === status?.glossary}>
									Save glossary
								</button>
							</section>

							<section className={styles.card}>
								<div className={styles.cardTitle}>Learn from recent chats</div>
								<p className={styles.cardHint}>
									Claude reads the last 30 days of chats (tables used, steps that failed, common questions) and
									rewrites its own short notes, so new chats start faster. It can only edit that one notes file.
									{status?.lastLearnedAt ? ` Last run: ${fullTime(status.lastLearnedAt)}.` : ' Never run yet.'}
								</p>
								<button type='button' className={styles.btnPrimary} onClick={startLearn} disabled={bgRunning || busy}>
									{status?.learnRunning ? 'Learning…' : 'Learn now'}
								</button>
								{!!status?.learnedNotes && (
									<details className={styles.notesBox}>
										<summary>Current notes</summary>
										<MarkdownView text={status.learnedNotes} />
									</details>
								)}
							</section>

							<section className={`${styles.card} ${styles.dangerCard}`}>
								<div className={styles.cardTitle}>Reset Server Chat</div>
								<p className={styles.cardHint}>
									Wipes everything Server Chat has made, for every user: chats, uploaded files, Claude's files, pages,
									scratch files, repo guides and repo copies. This can't be undone. Kept: the glossary, learned notes,
									extra notes and saved logins. After a reset, run Setup again.
								</p>
								{resetSummary && (
									<div className={styles.resetCounts}>
										{resetSummary.chats ?? 0} chats · {resetSummary.files ?? 0} files ({fmtSize(resetSummary.bytes)}) · {resetSummary.pages ?? 0} pages · {resetSummary.repos ?? 0} repo copies
									</div>
								)}
								<label className={styles.block}>
									Type RESET to confirm
									<input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder='RESET' autoComplete='off' />
								</label>
								<button type='button' className={styles.btnDanger} onClick={doReset}
									disabled={resetText !== 'RESET' || busy || bgRunning || !!status?.busy}
									title={busy || status?.busy ? 'Wait for the current answer to finish' : ''}>
									Reset everything
								</button>
								{resetDone && <div className={styles.libsOk}>Server Chat was reset. Run Setup above to start again.</div>}
								{Array.isArray(resetDone) && <div className={styles.errText}>Couldn't delete: {resetDone.join('; ')}</div>}
							</section>
						</div>
					</div>
				</div>
			)}

			{zoom && <div className={styles.zoom} onClick={() => setZoom(null)}><img src={zoom} alt='zoomed' /></div>}
		</div>
	);
}
